/* eslint-disable max-len */
import { useUser } from "providers/user";
import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { IconButton, Popover, Theme, useMediaQuery } from "@mui/material";
import { ArrowLeftIcon, ArrowRightIcon } from "assets/Icons";
import { Calendar } from "@react-admin/ra-calendar";
import CalendarDatesSelect from "./CalendarDatesSelect";
import dayjs, { Dayjs } from "dayjs";

const CustomReportingCalendar = ({
  calendarsDate,
  setCalendarsDate,
  calendarsDateTitle,
  setSelectedCalendarDate,
  setSelectedView,
  selectedView,
  className,
  timezone
}: {
  calendarsDate: Dayjs | null;
  setCalendarsDate: (date: Dayjs | null) => void;
  calendarsDateTitle: string | null;
  setSelectedCalendarDate: Dispatch<SetStateAction<any>>;
  setSelectedView: Dispatch<SetStateAction<string>>;
  selectedView: string;
  className?: string;
  timezone?: string;
}) => {
  const { user } = useUser();
  const popoverRef = useRef<HTMLDivElement>(null);
  const [activeNote, setActiveNote] = useState<string>("");
  const [isPrev, setIsPrev] = useState<boolean>(false);
  const [isNext, setIsNext] = useState<boolean>(false);
  const calRef = useRef<any>(null);
  const [dateAnchorEl, setDateAnchorEl] = useState<HTMLButtonElement | null>(
    null
  );
  const isSmall = useMediaQuery<Theme>("(max-width:599px)");

  // Navigation only — date reading happens in Calendar's datesSet callback
  useEffect(() => {
    if (calRef.current) {
      if (isPrev) {
        calRef?.current?.prev();
      }
      if (isNext) {
        calRef?.current?.next();
      }
      if (calendarsDate) {
        calRef?.current?.gotoDate(calendarsDate?.format());
        setCalendarsDate(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calRef, isPrev, isNext, calendarsDate]);

  const handleDatePopoverClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setDateAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setActiveNote("");
  };

  const handleDatePopoverClose = () => {
    setDateAnchorEl(null);
  };

  const datePopoverOpen = Boolean(dateAnchorEl);
  const datePopoverId = datePopoverOpen ? "simple-date-popover" : undefined;

  return (
    <>
      <div
        ref={popoverRef}
        onClick={() => !!activeNote && handlePopoverClose()}
        aria-hidden
      >
        <div
          className={
            className
              ? className
              : `absolute ${
                  user.impersonate_company_id ? "top-[51px]" : "top-[21px]"
                } ${
                  isSmall ? "right-[64px]" : "right-[124px]"
                } flex gap-[16px] z-50 cursor-default`
          }
        >
          <div className="flex bg-[#FAF9F7] border border-[rgba(48,47,46,0.23)] rounded-[32px]">
            <IconButton
              sx={{
                borderRadius: "0px",
                borderTopLeftRadius: "32px",
                borderBottomLeftRadius: "32px"
              }}
              className="border-none p-[0px] w-[36px] h-[36px] font-['Inter'] font-[500] text-[14px] text-mono-primaryTextColor"
              onClick={() => {
                setIsPrev(true);
                setTimeout(() => {
                  setIsPrev(false);
                }, 0);
              }}
            >
              <ArrowLeftIcon />
            </IconButton>
            <button
              className="border-none h-[36px] flex justify-center px-[12px] items-center font-['Inter'] font-[500] text-[14px] text-mono-primaryTextColor hover:bg-[rgba(0,0,0,0.04)]"
              onClick={handleDatePopoverClick}
            >
              {calendarsDateTitle}
            </button>
            <IconButton
              sx={{
                borderRadius: "0px",
                borderTopRightRadius: "32px",
                borderBottomRightRadius: "32px"
              }}
              className="border-none p-[0px] w-[36px] h-[36px] font-['Inter'] font-[500] text-[14px] text-mono-primaryTextColor"
              onClick={() => {
                setIsNext(true);
                setTimeout(() => {
                  setIsNext(false);
                }, 0);
              }}
            >
              <ArrowRightIcon />
            </IconButton>
          </div>
        </div>
        <div className="w-max h-max">
          <div className="flex">
            <Calendar
              datesSet={(dateInfo) => {
                calRef.current = dateInfo?.view?.calendar;
                setSelectedCalendarDate({
                  title: dateInfo?.view?.title,
                  dateForFilter: JSON.parse(
                    JSON.stringify(dayjs(dateInfo?.view?.currentStart))
                  )
                });
              }}
              mutationMode="optimistic"
              height="3000px"
              headerToolbar={{
                left: "",
                center: "",
                right: "timeGridDay"
              }}
              sx={{ display: "none" }}
              initialView="timeGridDay"
              nowIndicator={true}
              slotDuration="00:15:00"
              slotLabelInterval="01:00"
            />
          </div>
        </div>
      </div>
      {calRef?.current && (
        <Popover
          id={datePopoverId}
          open={datePopoverOpen}
          anchorEl={dateAnchorEl}
          onClose={handleDatePopoverClose}
          anchorOrigin={{
            vertical: "center",
            horizontal: "center"
          }}
          transformOrigin={{
            vertical: -25,
            horizontal: "center"
          }}
        >
          <CalendarDatesSelect
            calendarApi={calRef.current}
            handleDatePopoverClose={handleDatePopoverClose}
            selectedView={selectedView}
            setSelectedView={setSelectedView}
            setCalendarsDate={setCalendarsDate}
            localStorageKey="ReportDateFilter"
            timezone={timezone}
          />
        </Popover>
      )}
    </>
  );
};

export default CustomReportingCalendar;
